import type { AnomalyDetail, ComplianceResult, WeldAnalyticsBundle, WeldInsightCard, WeldSummary } from '../types/weld';
import { aggregateByWeldingSystem } from './systemAggregates';

function prepData(bundle: WeldAnalyticsBundle) {
  const anomalies = bundle.anomalies;
  const welds = bundle.welds;
  const comp = bundle.compliance;
  const totalPass = bundle.totalPasses || 0;
  const wpsFailed = bundle.wpsFailed || 0;
  const projAvgPct = totalPass ? (wpsFailed / totalPass) * 100 : 0;

  const jobAnom: Record<
    string,
    { welds: Set<string>; passes: number; critical: number; ml: number; wps: number }
  > = {};
  anomalies.forEach((a) => {
    if (!jobAnom[a.job_number]) {
      jobAnom[a.job_number] = { welds: new Set(), passes: 0, critical: 0, ml: 0, wps: 0 };
    }
    const j = jobAnom[a.job_number];
    j.welds.add(a.weld_number);
    j.passes += 1;
    if (a.review_priority === 'Critical') j.critical += 1;
    if (a.anomaly_source === 'ML Hidden') j.ml += 1;
    if (a.anomaly_source === 'WPS' || a.anomaly_source === 'WPS+ML') j.wps += 1;
  });

  const passAnom: Record<string, { passes: number; critical: number }> = {};
  anomalies.forEach((a) => {
    const pf = a.pass_family || a.pass_name || 'Unknown';
    if (!passAnom[pf]) passAnom[pf] = { passes: 0, critical: 0 };
    passAnom[pf].passes += 1;
    if (a.review_priority === 'Critical') passAnom[pf].critical += 1;
  });

  const sysEntries = aggregateByWeldingSystem(welds);
  const jobEntries = Object.entries(jobAnom).sort((a, b) => b[1].passes - a[1].passes);
  const passEntries = Object.entries(passAnom).sort((a, b) => b[1].passes - a[1].passes);

  return {
    anomalies,
    welds,
    comp,
    totalPass,
    wpsFailed,
    projAvgPct,
    jobEntries,
    passEntries,
    sysEntries,
  };
}

export function generateWeldInsights(bundle: WeldAnalyticsBundle | null): WeldInsightCard[] {
  if (!bundle?.welds?.length && !bundle?.anomalies?.length) return [];

  const D = prepData(bundle);
  const insights: WeldInsightCard[] = [];

  D.jobEntries.slice(0, 3).forEach(([job, v]) => {
    const rate = v.welds.size > 0 ? (v.passes / v.welds.size) * 100 : 0;
    const pri = rate >= 10 ? 'HIGH' : rate >= 3 ? 'MEDIUM' : 'LOW';
    insights.push({
      category: 'Patterns',
      priority: pri,
      confidence: 90,
      title: `Out of WPS limit concentration: Job ${job}`,
      why: `Job has ${v.passes} flagged passes across ${v.welds.size} welds.`,
      body: `Job ${job}: ${v.passes} anomalous passes, ${v.critical} critical, ${v.ml} ML-hidden.`,
      affects: v.passes,
      recommendation: `Review WPS and parameters for job ${job}.`,
      details: { Job: job, 'Anomalous passes': v.passes, Critical: v.critical },
      drillDown: { kind: 'job', jobNumber: job },
    });
  });

  D.passEntries.slice(0, 2).forEach(([pf, v]) => {
    const pct = D.anomalies.length ? ((v.passes / D.anomalies.length) * 100).toFixed(1) : '0';
    insights.push({
      category: 'Patterns',
      priority: Number(pct) >= 40 ? 'HIGH' : 'MEDIUM',
      confidence: 85,
      title: `${pf} pass anomaly pattern (${pct}% of flags)`,
      why: `${pf} accounts for ${pct}% of flagged passes.`,
      body: `${v.passes} anomalous ${pf} passes, ${v.critical} critical.`,
      affects: v.passes,
      recommendation: `Audit ${pf} pass parameters across affected jobs.`,
      details: { 'Pass family': pf, Passes: v.passes, Critical: v.critical },
    });
  });

  const worstWeld = D.welds.reduce<WeldSummary | null>(
    (m, w) => (!m || (w.composite_score || 0) > (m.composite_score || 0) ? w : m),
    null,
  );
  if (worstWeld) {
    insights.push({
      category: 'Patterns',
      priority: 'HIGH',
      confidence: 93,
      title: `Highest risk weld #${worstWeld.weld_number}`,
      why: 'Highest composite score in the sample.',
      body: `Score ${(worstWeld.composite_score || 0).toFixed(4)}, ${worstWeld.anomalous_passes || 0} anomalous passes.`,
      affects: 1,
      recommendation: `Prioritize NDT / inspection for weld ${worstWeld.weld_number}.`,
      details: {
        Weld: worstWeld.weld_number,
        Job: worstWeld.job_number,
        Score: (worstWeld.composite_score || 0).toFixed(4),
      },
      drillDown: {
        kind: 'weld',
        jobNumber: worstWeld.job_number,
        weldNumber: worstWeld.weld_number,
      },
    });
  }

  const mlHidden = D.anomalies.filter((a) => a.anomaly_source === 'ML Hidden').length;
  const critCount = D.anomalies.filter((a) => a.review_priority === 'Critical').length;
  insights.push({
    category: 'Anomalies',
    priority: mlHidden > 50 ? 'HIGH' : 'MEDIUM',
    confidence: 88,
    title: `${mlHidden} ML-hidden anomalies`,
    why: 'Passes flagged by ML without WPS violation.',
    body: `${mlHidden} ML-only flags; ${critCount} critical overall.`,
    affects: mlHidden,
    recommendation: 'Add ML-hidden welds to the next inspection cycle.',
    details: { 'ML hidden': mlHidden, Critical: critCount, Total: D.anomalies.length },
  });

  D.sysEntries.slice(0, 3).forEach((sys) => {
    const anomRate = sys.welds > 0 ? ((sys.anomalous / sys.welds) * 100).toFixed(1) : '0';
    insights.push({
      category: 'System Performance',
      priority: Number(anomRate) >= 30 ? 'HIGH' : Number(anomRate) >= 15 ? 'MEDIUM' : 'LOW',
      confidence: 77,
      title: `System ${sys.weldingSystem} — ${anomRate}% anomalous`,
      why: `Welding system ${sys.weldingSystem} anomaly rate ${anomRate}%.`,
      body: `${sys.anomalous} of ${sys.welds} welds with flagged passes. Avg score ${sys.avgScore?.toFixed(4) ?? '—'}.`,
      affects: sys.anomalous,
      recommendation:
        Number(anomRate) >= 30
          ? `Calibrate system ${sys.weldingSystem} immediately.`
          : `Schedule parameter audit for ${sys.weldingSystem}.`,
      details: {
        System: sys.weldingSystem,
        'Anomaly rate': `${anomRate}%`,
        Welds: sys.welds,
      },
    });
  });

  const wpsParam = D.comp?.per_parameter?.[0]?.parameter || 'heat_avg';
  insights.push({
    category: 'Root Cause Analysis',
    priority: 'MEDIUM',
    confidence: 90,
    title: `WPS focus: ${wpsParam}`,
    why: 'Formal WPS failures stem from configured parameter checks.',
    body: `${D.wpsFailed} passes out of WPS limit across ${D.totalPass} passes (${D.projAvgPct.toFixed(2)}% out of limit).`,
    affects: D.wpsFailed,
    recommendation: 'Review whether heat-only WPS mode is sufficient for this job.',
    details: {
      Parameter: wpsParam,
      'Out of limit': D.wpsFailed,
      'Out of limit rate': `${D.projAvgPct.toFixed(2)}%`,
    },
  });

  if (D.comp?.overall_compliance_rate_pct != null) {
    insights.push({
      category: 'Comparative Analysis',
      priority: D.comp.overall_compliance_rate_pct < 95 ? 'HIGH' : 'MEDIUM',
      confidence: 82,
      title: `Overall WPS compliance ${D.comp.overall_compliance_rate_pct.toFixed(1)}%`,
      why: 'Job-level compliance summary from WeldIQ pipeline.',
      body: `${D.comp.total_passes ?? D.totalPass} passes checked.`,
      affects: D.comp.total_passes ?? D.totalPass,
      recommendation: 'Drill into per-parameter compliance in Statistics.',
      details: {
        'Compliance rate': `${D.comp.overall_compliance_rate_pct.toFixed(1)}%`,
        'Total passes': D.comp.total_passes ?? D.totalPass,
      },
    });
  }

  return insights;
}

export const INSIGHT_CATEGORIES = [
  'Patterns',
  'Correlations',
  'Anomalies',
  'Temporal Trends',
  'System Performance',
  'Root Cause Analysis',
  'Comparative Analysis',
] as const;
